// ═══════════════════════════════════════════════════════════════════
//  modules/erp/PlayerMetiersTab.js
//  PlayerMetiersTab
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { MetierXpBar } from "./MetierXpBar";
import { argent, dateCourte, duree, METIERS, metierInfo, NATURES_RECOMPENSE, nombre, RARETES_FIOLE } from "./metiers";

export function PlayerMetiersTab({ uuid, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/players/${uuid}/metiers`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setData(json);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, uuid]);
  useEffect(() => { load(); }, [uuid, load]);

  if (loading) return <div style={{ color: THEME.textMuted }}>Chargement des métiers...</div>;
  if (!data) return <div style={{ color: THEME.red }}>Erreur de chargement.</div>;

  if (data.unavailable) return (
    <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
      ⏳ Le module Métiers n'est pas encore disponible (tables <code>oj_*</code> absentes).
      Elles sont créées au premier démarrage du plugin OrialisJobs sur cette base.
    </div>
  );

  const metiers = data.metiers || [];
  const t = data.totaux || {};
  const incoherents = metiers.filter((m) => m.connu && !m.coherent);

  // Les récompenses arrivent à plat, les plus récentes d'abord. On les
  // regroupe par métier : un palier atteint donne souvent plusieurs lignes
  // (argent + objet + permission), et les voir éparpillées par date donne
  // l'impression d'un désordre qui n'existe pas.
  const parMetier = {};
  (data.recompenses || []).forEach((r) => {
    (parMetier[r.metier] = parMetier[r.metier] || []).push(r);
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── L'avertissement, s'il y a lieu ────────────────────────── */}
      {/* Il passe AVANT tout le reste : une barre fausse qui a l'air */}
      {/* juste est pire qu'une absence de barre.                      */}
      {incoherents.length > 0 && (
        <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 16px", fontSize: 12, lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ La courbe d'XP de l'intranet ne correspond plus à ce que le plugin écrit</div>
          {incoherents.map((m) => (
            <div key={m.metier}>
              {metierInfo(m.metier).label} : niveau <strong>{m.niveau}</strong> en base,
              {" "}<strong>{m.niveau_calcule}</strong> d'après la courbe recopiée dans l'intranet.
            </div>
          ))}
          <div style={{ marginTop: 6, color: THEME.textMuted }}>
            Le niveau affiché reste celui du plugin — c'est lui qui fait foi en jeu.
            Seule la barre de progression est douteuse. Lancer
            {" "}<code>outils\intranet\POSER_METIERS.bat</code>, choix 3.
          </div>
        </div>
      )}

      {/* ── La fiole en cours ─────────────────────────────────────── */}
      {data.boost && (() => {
        const r = RARETES_FIOLE[data.boost.rarete] || { label: data.boost.rarete, color: THEME.accent };
        return (
          <div style={{ background: THEME.surface, border: `1px solid ${r.color}`, borderRadius: 8, padding: "12px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
              <span style={{ color: r.color, fontSize: 13, fontWeight: 700 }}>🧪 Fiole {r.label} en cours</span>
              <span style={{ color: THEME.text, fontSize: 12 }}>
                {duree(data.boost.secondes_restantes)} restantes sur {duree(data.boost.duree_totale_accordee)}
              </span>
            </div>
            <div style={{ background: THEME.border, borderRadius: 4, height: 6, overflow: "hidden" }}>
              <div style={{ background: r.color, width: `${Math.min(100, data.boost.pourcent)}%`, height: "100%" }} />
            </div>
            <div style={{ color: THEME.textDim, fontSize: 10, marginTop: 6 }}>
              Le minuteur est en pause hors connexion : ce sont des minutes de jeu, pas des minutes d'horloge.
              Le boost est global — il vaut pour les huit métiers.
            </div>
          </div>
        );
      })()}

      {/* ── Le récapitulatif ──────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        {[
          ["Métiers commencés", `${t.metiers_commences || 0} / ${METIERS.length}`],
          ["Niveau le plus haut", nombre(t.niveau_le_plus_haut || 0)],
          ["XP en cours", nombre(t.xp || 0, 1)],
          ["Argent gagné", argent(t.argent_gagne || 0)],
          ["Actions (carrière)", nombre(t.actions || 0)],
        ].map(([label, valeur]) => (
          <div key={label} style={{ background: THEME.surface, borderRadius: 6, padding: "10px 14px" }}>
            <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>{label.toUpperCase()}</div>
            <div style={{ color: THEME.text, fontSize: 15, fontWeight: 700 }}>{valeur}</div>
          </div>
        ))}
      </div>

      {/* ── Les huit métiers ──────────────────────────────────────── */}
      {/* Les huit sont affichés, y compris ceux jamais commencés :   */}
      {/* « ce joueur n'a jamais touché à la pêche » est une info, et  */}
      {/* une case absente ne la donne pas.                           */}
      <div>
        <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 10, textTransform: "uppercase" }}>Les huit métiers</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 12, alignItems: "start" }}>
          {metiers.map((m) => {
            const info = metierInfo(m.metier);
            const vide = !m.connu;
            const recs = parMetier[m.metier] || [];
            return (
              <div key={m.metier} style={{
                background: vide ? THEME.surface : THEME.panel,
                border: `1px solid ${m.connu && !m.coherent ? THEME.amber : THEME.border}`,
                borderRadius: 10, padding: 14, opacity: vide ? 0.55 : 1,
                display: "flex", flexDirection: "column", gap: 8,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 22 }}>{info.emoji}</span>
                  <span style={{ color: vide ? THEME.textDim : THEME.text, fontSize: 13, fontWeight: 700 }}>{info.label}</span>
                  {m.prestige && m.prestige.niveau_prestige > 0 && (
                    <span style={{ marginLeft: "auto", background: THEME.goldDim, color: THEME.gold, fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4 }}>
                      ★ {m.prestige.niveau_prestige}
                    </span>
                  )}
                </div>

                {vide ? (
                  <div style={{ color: THEME.textDim, fontSize: 11 }}>Jamais commencé</div>
                ) : (
                  <>
                    <MetierXpBar niveau={m.niveau} progression={m.progression} color={info.color} incoherent={!m.coherent} />

                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: THEME.textMuted, borderTop: `1px solid ${THEME.border}`, paddingTop: 8 }}>
                      <span title="Remise à zéro au prestige — la carrière, elle, ne l'est jamais">XP</span>
                      <span style={{ color: THEME.text }}>{nombre(m.xp, 1)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: THEME.textMuted }}>
                      <span>Argent gagné</span>
                      <span style={{ color: THEME.emerald }}>{argent(m.argent_gagne)}</span>
                    </div>

                    {/* La carrière survit au prestige, l'XP courante non.  */}
                    {/* On ne l'affiche que si elle DIFFÈRE : sinon elle    */}
                    {/* répète la ligne du dessus et n'apprend rien.        */}
                    {m.carriere && (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: THEME.textMuted }}>
                          <span>Actions</span>
                          <span style={{ color: THEME.text }}>{nombre(m.carriere.actions)}</span>
                        </div>
                        {Math.abs(m.carriere.xp_totale - m.xp) > 0.001 && (
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: THEME.textMuted }}>
                            <span title="Jamais remise à zéro, même au prestige">XP de carrière</span>
                            <span style={{ color: THEME.gold }}>{nombre(m.carriere.xp_totale, 1)}</span>
                          </div>
                        )}
                      </>
                    )}

                    {recs.length > 0 && (
                      <div style={{ color: THEME.textDim, fontSize: 10, marginTop: 2 }}>
                        🎁 {recs.length} récompense{recs.length > 1 ? "s" : ""} de palier reçue{recs.length > 1 ? "s" : ""}
                      </div>
                    )}
                    <div style={{ color: THEME.textDim, fontSize: 10 }}>Mis à jour le {dateCourte(m.maj_le)}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Les récompenses de palier remises ─────────────────────── */}
      {(data.recompenses || []).length > 0 && (
        <div>
          <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 10, textTransform: "uppercase" }}>
            Récompenses de palier remises
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 520 }}>
              <thead>
                <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                  {["Métier", "Niveau", "Nature", "Ce qui a été remis", "Le"].map((h) => (
                    <th key={h} style={{ padding: "6px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.recompenses.map((r, i) => {
                  const info = metierInfo(r.metier);
                  const nat = NATURES_RECOMPENSE[r.nature] || { label: r.nature, emoji: "•" };
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                      <td style={{ padding: "6px 10px", color: THEME.text }}>{info.emoji} {info.label}</td>
                      <td style={{ padding: "6px 10px", color: THEME.text }}>{r.niveau}</td>
                      <td style={{ padding: "6px 10px", color: THEME.textMuted }}>{nat.emoji} {nat.label}</td>
                      <td style={{ padding: "6px 10px", color: THEME.text }}>
                        {/* Le libellé vient du catalogue, qui peut avoir changé  */}
                        {/* depuis. Le registre, lui, ne bouge jamais : quand le  */}
                        {/* catalogue ne dit plus rien, on montre l'identifiant   */}
                        {/* brut plutôt qu'une case vide.                         */}
                        {r.libelle
                          || (r.nature === "ARGENT" ? argent(r.montant) : null)
                          || (r.identifiant ? `${r.identifiant}${r.quantite > 1 ? ` × ${r.quantite}` : ""}` : "—")}
                      </td>
                      <td style={{ padding: "6px 10px", color: THEME.textMuted }}>{dateCourte(r.donnee_le)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Le pied de page ───────────────────────────────────────── */}
      <div style={{ color: THEME.textDim, fontSize: 10, lineHeight: 1.7, borderTop: `1px solid ${THEME.border}`, paddingTop: 12 }}>
        Lecture seule. Les tables <code>oj_*</code> sont écrites par le plugin OrialisJobs, qui garde
        ses joueurs en cache mémoire et verse en base toutes les cinq minutes — un joueur connecté
        peut donc avoir jusqu'à cinq minutes d'avance sur cet écran.
        {data.joueur && (
          <> Vu par le plugin du {dateCourte(data.joueur.premiere_vue)} au {dateCourte(data.joueur.derniere_vue)}.</>
        )}
      </div>
    </div>
  );
}
